import { useEffect, useRef, useState } from 'react';
import type { FormEvent, KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage, hasCode } from '../lib/api-error';
import { toast } from '../lib/toast';
import {
  toDisplayMessages,
  useAssistantConversation,
  useAssistantConversations,
  useAssistantTurn,
  type AssistantStep,
} from '../lib/assistant-api';
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  Spinner,
  Textarea,
} from '../components/ui';

/**
 * YAPAY ZEKÂ ASİSTANI — sohbet ekranı.
 *
 * ⚠ ASİSTAN HİÇBİR YASAL BELGE DÜZENLEMEZ. En fazla TASLAK üretir
 * (`fatura_taslagi_olustur` — numara vermez) ya da OKUR. Bunu doğrulamak
 * ya da onaylamak insana kalır; bu yüzden bir taslak oluştuğunda ekran
 * "İncele →" bağlantısı gösterir, kendisi hiçbir şeyi "kesmez".
 *
 * ⚠ AKIŞ (STREAMING) YOK — bilinçli bir ilk sürüm sınırı. Panelde SSE/
 * WebSocket altyapısı henüz kurulmadı; bir tur tek bir JSON yanıtla döner ve
 * o turda çağrılan araçlar `steps` listesiyle görünür olur. Uzun bir tur
 * birkaç saniye sürebilir — düğme bu sürede `loading` kalır.
 */
export function AssistantPage() {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [lastSteps, setLastSteps] = useState<AssistantStep[]>([]);
  const listRef = useRef<HTMLDivElement>(null);

  const conversations = useAssistantConversations();
  const active = useAssistantConversation(activeId);
  const turn = useAssistantTurn();

  const messages = active.data ? toDisplayMessages(active.data.messages) : [];

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages.length, pendingText]);

  /**
   * ⚠ GÖNDERİLEN MESAJ, TUR BİTİNCE DEĞİL, SOHBET DETAYI TAZELENİNCE SİLİNİR.
   *
   * Ekrandaki yazışma yalnız KAYITLI satırlardan çizilir (`messages`);
   * `pendingText` ise henüz kaydedilmemiş mesajı geçici olarak gösterir.
   * `onSuccess`te hemen temizlenirse, sohbet detayını yeniden çeken GET hâlâ
   * uçuşta olduğu için o aralıkta mesaj HİÇBİR YERDE görünmez — kullanıcı az
   * önce yazdığı satırın kaybolup birkaç yüz ms sonra geri geldiğini görür.
   * (Tazeleme sırasında `isLoading` de `false`tur, yani spinner da örtmez.)
   */
  useEffect(() => {
    if (pendingText === null || turn.isPending || !activeId) return;
    if (active.isFetching) return;
    if (active.data || active.isError) setPendingText(null);
  }, [pendingText, turn.isPending, activeId, active.isFetching, active.data, active.isError]);

  function submit(e: FormEvent): void {
    e.preventDefault();
    const text = draft.trim();
    if (!text || turn.isPending) return;

    setDraft('');
    setPendingText(text);
    setLastSteps([]);

    turn.mutate(
      { conversationId: activeId ?? undefined, message: text },
      {
        onSuccess: (result) => {
          // ⚠ `pendingText` BURADA TEMİZLENMEZ — yukarıdaki etkiye bakın.
          setLastSteps(result.steps);
          if (!activeId) setActiveId(result.conversationId);
        },
        onError: (err) => {
          setPendingText(null);
          // Kurulum ile ilgili üç kod ayrıca bir banner'da gösteriliyor —
          // burada TEKRAR toast basmak aynı şeyi iki kez söylemek olurdu.
          if (
            !hasCode(
              err,
              'ASSISTANT_DISABLED',
              'ASSISTANT_NO_API_KEY',
              'ASSISTANT_DAILY_CAP_REACHED',
            )
          ) {
            toast.error(err);
          }
        },
      },
    );
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e);
    }
  }

  const setupError =
    turn.isError &&
    hasCode(turn.error, 'ASSISTANT_DISABLED', 'ASSISTANT_NO_API_KEY', 'ASSISTANT_DAILY_CAP_REACHED')
      ? turn.error
      : null;

  return (
    <>
      <PageHeader
        title="Asistan"
        description="Yazarak sor: bir faturayı bul, KDV durumunu özetle ya da yeni bir fatura taslağı hazırla. Taslağı gerçek faturaya çevirmek panelde senin onayını gerektirir."
      />

      <div className="flex gap-4" style={{ height: 'calc(100vh - 220px)', minHeight: 420 }}>
        {/* ── Sohbet listesi ─────────────────────────────────────────── */}
        <Card className="w-56 shrink-0 overflow-y-auto">
          <div className="border-b border-[var(--c-border)] p-2">
            <Button
              size="sm"
              variant="secondary"
              className="w-full"
              onClick={() => {
                setActiveId(null);
                setLastSteps([]);
              }}
            >
              + Yeni sohbet
            </Button>
          </div>
          <nav className="p-1">
            {(conversations.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  setActiveId(c.id);
                  setLastSteps([]);
                }}
                className={[
                  'block w-full truncate rounded-[var(--r-md)] px-2 py-1.5 text-left text-[13px]',
                  c.id === activeId
                    ? 'bg-[var(--c-brand-soft)] font-medium text-[var(--c-brand)]'
                    : 'text-[var(--c-ink-soft)] hover:bg-[var(--c-surface-alt)]',
                ].join(' ')}
                title={c.title || 'Adsız sohbet'}
              >
                {c.title || 'Adsız sohbet'}
              </button>
            ))}
            {conversations.data?.length === 0 && (
              <p className="px-2 py-3 text-xs text-[var(--c-ink-faint)]">Henüz sohbet yok.</p>
            )}
          </nav>
        </Card>

        {/* ── Aktif sohbet ───────────────────────────────────────────── */}
        <Card className="flex flex-1 flex-col overflow-hidden">
          <div ref={listRef} className="flex-1 space-y-3 overflow-y-auto p-4">
            {activeId && active.isLoading && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}
            {activeId && active.isError && <ErrorState message={errorMessage(active.error)} />}
            {!activeId && messages.length === 0 && pendingText === null && (
              <EmptyState
                title="Bir şey sor"
                description={
                  'Örnek: "La Perle Bleue bana ne kadar borçlu?" ya da ' +
                  '"X şirketine 100 euro aylık abonelik faturası taslağı hazırla".'
                }
              />
            )}

            {messages.map((m) => (
              <MessageBubble key={m.key} role={m.role} text={m.text} toolCalls={m.toolCalls} />
            ))}

            {pendingText !== null && (
              <MessageBubble role="user" text={pendingText} toolCalls={[]} />
            )}
            {turn.isPending && (
              <div className="flex items-center gap-2 text-xs text-[var(--c-ink-faint)]">
                <Spinner size={13} />
                düşünüyor…
              </div>
            )}

            {lastSteps.length > 0 && <StepsPanel steps={lastSteps} />}
          </div>

          {setupError && (
            <div className="border-t border-[var(--c-border)] bg-[var(--c-warn-soft)] px-4 py-3 text-sm text-[var(--c-warn)]">
              {errorMessage(setupError)}{' '}
              <Link to="/settings" className="font-medium underline">
                Ayarlar →
              </Link>
            </div>
          )}

          <form onSubmit={submit} className="border-t border-[var(--c-border)] p-3">
            <div className="flex items-end gap-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Bir şey yaz… (Enter gönderir, Shift+Enter yeni satır)"
                rows={2}
                disabled={turn.isPending}
                className="flex-1"
              />
              <Button
                type="submit"
                disabled={turn.isPending || draft.trim() === ''}
                loading={turn.isPending}
              >
                Gönder
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </>
  );
}

function MessageBubble({
  role,
  text,
  toolCalls,
}: {
  role: 'user' | 'assistant';
  text: string;
  toolCalls: Array<{ name: string }>;
}) {
  const mine = role === 'user';
  return (
    <div className={mine ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={[
          'max-w-[75%] rounded-[var(--r-lg)] px-3 py-2 text-sm whitespace-pre-wrap',
          mine ? 'bg-[var(--c-brand)] text-white' : 'bg-[var(--c-surface-alt)] text-[var(--c-ink)]',
        ].join(' ')}
      >
        {toolCalls.length > 0 && (
          <p className="mb-1 text-[11px] opacity-75">
            🔧 {toolCalls.map((t) => t.name).join(', ')}
          </p>
        )}
        {text || <span className="italic opacity-60">(yanıt boş döndü)</span>}
      </div>
    </div>
  );
}

/**
 * SON TURUN ADIM ÖZETİ — ⚠ TASLAK üreten bir araç varsa "İncele →"
 * bağlantısı burada durur. Asistan hiçbir zaman "faturayı kestim" demez;
 * gerçek düzenleme adımı bu bağlantının GÖTÜRDÜĞÜ sayfada, insan onayıyla
 * olur.
 */
function StepsPanel({ steps }: { steps: AssistantStep[] }) {
  return (
    <div className="rounded-[var(--r-md)] border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2 text-xs">
      <p className="mb-1 font-medium text-[var(--c-ink-soft)]">Bu turda çalışan araçlar</p>
      <ul className="space-y-1">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center gap-2">
            <span className={s.ok ? 'text-[var(--c-ok)]' : 'text-[var(--c-danger)]'}>
              {s.ok ? '✓' : '✕'}
            </span>
            <span>{s.tool}</span>
            {s.deepLink && (
              <Link to={s.deepLink} className="font-medium text-[var(--c-brand)] underline">
                İncele →
              </Link>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
